import { FormEvent, useContext, useState } from "react";

import { TemplateStepConstants } from "../../../../../../../../../../../constants";
import { ServiceError, Status } from "../../../../../../../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../../../../../../../service/ValidationErrorFormatter";
import { addOrRemoveWorkflowTemplateStep } from "../../../../../../../../../../../service/workflow";
import { useIsLoadingWrapper } from "../../../../../../../../../../../utils";
import { Toast } from "../../../../../../../../../../../widget";
import { WorkflowTemplateContext } from "../../../../../../WorkflowTemplateContext";

interface UseDeleteTemplateStepModalProps {
  closeModal: () => void;
  templateUuid?: string;
  templateStepUuid?: string;
  templateStepRowVersion: number;
}

interface UseDeleteTemplateStepModalReturnData {
  handleDeleteTemplateStep: (e: FormEvent<HTMLFormElement>) => void;
  isHandleDeleteTemplateStepLoading: boolean;
  onCancel: () => void;
  errors: ServiceError[];
}

export const useDeleteTemplateStepModal = ({
  closeModal,
  templateUuid,
  templateStepUuid,
  templateStepRowVersion,
}: UseDeleteTemplateStepModalProps): UseDeleteTemplateStepModalReturnData => {
  const { shouldRefreshWorkflowTemplate } = useContext(WorkflowTemplateContext);
  const [isHandleDeleteTemplateStepLoading, setIsHandleDeleteTemplateStepLoading] = useState(false);
  const [errors, setErrors] = useState<ServiceError[]>([]);

  const onCancel = (): void => {
    setErrors([]);
    closeModal();
  };

  const handleDeleteTemplateStep = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    if (!templateStepUuid) {
      setErrors([
        {
          code: "NO_TEMPLATE_STEP",
          message: "A template step must be selected",
        },
      ]);
    } else if (!templateUuid) {
      setErrors([
        {
          code: "NO_TEMPLATE",
          message: "A template must be selected",
        },
      ]);
    } else {
      await addOrRemoveWorkflowTemplateStep({
        workflowTemplateUuid: templateUuid,
        workflowTemplateStepUuid: templateStepUuid,
        workflowTemplateStepRowVersion: templateStepRowVersion,
        operation: TemplateStepConstants.REMOVE_OPERATION,
        defaultIsRepeatable: null,
        defaultPhase: null,
        defaultStepName: null,
      }).then((response) => {
        if (response.status === Status.Success) {
          Toast.success({ message: "Template step successfully deleted" });

          shouldRefreshWorkflowTemplate();
          onCancel();
        }
        if (response.status === Status.Error && response.errors) {
          setErrors(response.errors.map((error) => ({ ...error, message: getErrorMessageFromCode(error.code) })));
        }
      });
    }
  }, setIsHandleDeleteTemplateStepLoading);

  return { isHandleDeleteTemplateStepLoading, handleDeleteTemplateStep, onCancel, errors };
};
